import { type FormEvent, useEffect, useState } from 'react';
import { BADGES, type HelperProfileResponse, type LeaderboardResponse, NICKNAME_MAX, POINTS, levelFor } from '@notlost/shared';
import { ShieldCheck } from '../components/Brand';
import { Page } from '../components/Layout';
import { getHelper, getLeaderboard, setNickname } from '../lib/api';
import { getHelperId } from '../lib/helper';
import { t } from '../i18n';

/** The finder's anonymous helper card: points, level, badges, opt-in leaderboard name. */
export function Helper() {
  const [helperId] = useState(() => getHelperId(false));
  const [profile, setProfile] = useState<HelperProfileResponse | null>(null);
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [nick, setNick] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getLeaderboard()
      .then(setBoard)
      .catch(() => setBoard({ entries: [] }));
    if (!helperId) return;
    getHelper(helperId)
      .then((p) => {
        setProfile(p);
        setNick(p.nickname ?? '');
      })
      .catch(() => setFailed(true));
  }, [helperId]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!helperId) return;
    try {
      const p = await setNickname(helperId, nick);
      setProfile(p);
      setNick(p.nickname ?? '');
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1500);
      setBoard(await getLeaderboard());
    } catch {
      setFailed(true);
    }
  }

  const level = levelFor(profile?.points ?? 0);

  return (
    <Page>
      <p className="eyebrow">{t('helper.eyebrow')}</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight tracking-tight">{t('helper.title')}</h1>
      <p className="mt-2 inline-flex items-start gap-2 text-sm text-muted">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue" />
        {t('helper.anon')}
      </p>

      {!helperId && <p className="card mt-6 p-5 text-body">{t('helper.none')}</p>}
      {failed && <p className="mt-4 text-sm font-semibold text-danger">{t('helper.loadFailed')}</p>}

      {helperId && (
        <>
          <section className="mt-6 overflow-hidden tile bg-night p-6 text-white shadow-lift">
            <p className="text-sm font-semibold text-night-muted">{t('helper.level', { level: level.level, name: level.name })}</p>
            <p className="mt-1 text-5xl font-semibold tracking-tight tabular-nums">{profile?.points ?? 0}</p>
            <p className="text-sm text-night-muted">{t('helper.points')}</p>
            <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-white/20">
              <div className="h-full rounded-full bg-amber transition-all" style={{ width: `${Math.round(level.progress * 100)}%` }} />
            </div>
            <p className="mt-2 text-sm text-night-muted">
              {level.nextAt !== null
                ? t('helper.next', { n: level.nextAt - (profile?.points ?? 0), name: level.nextName ?? '' })
                : t('helper.max')}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <Stat value={profile?.finds ?? 0} label={t('helper.finds')} />
              <Stat value={profile?.returns ?? 0} label={t('helper.returns')} />
            </div>
          </section>

          <section className="card mt-6 p-5">
            <h2 className="font-bold">{t('helper.badges')}</h2>
            <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {BADGES.map((b) => {
                const earned = profile?.badges.includes(b.id) ?? false;
                return (
                  <li
                    key={b.id}
                    className={`rounded-xl border p-3 ${earned ? 'border-amber bg-amber-50' : 'border-line bg-surface opacity-70'}`}
                  >
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full ${earned ? 'bg-amber text-ink' : 'bg-white text-muted'}`}>
                      <ShieldCheck className="h-5 w-5" />
                    </span>
                    <p className="mt-2 text-sm font-bold">{t(`helper.badge.${b.id}`)}</p>
                    <p className="text-xs text-muted">{t(`helper.badgeHint.${b.id}`)}</p>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="card mt-6 p-5">
            <h2 className="font-bold">{t('helper.how')}</h2>
            <ul className="mt-3 flex flex-col gap-2 text-body">
              <li>{t('helpers.points.find', { n: POINTS.find })}</li>
              <li>{t('helpers.points.return', { n: POINTS.returned })}</li>
            </ul>
          </section>

          <form onSubmit={save} className="card mt-6 flex flex-col gap-2 p-5">
            <label htmlFor="nick" className="text-sm font-semibold">
              {t('helper.nickname')}
            </label>
            <div className="flex gap-2">
              <input id="nick" maxLength={NICKNAME_MAX} value={nick} onChange={(e) => setNick(e.target.value)} className="field" />
              <button type="submit" className="btn btn-primary shrink-0">
                {saved ? t('helper.saved') : t('helper.save')}
              </button>
            </div>
            <span className="text-xs text-muted">{t('helper.nicknameHint')}</span>
          </form>
        </>
      )}

      <section className="card mt-6 p-5">
        <h2 className="font-bold">{t('helpers.board')}</h2>
        {board && board.entries.length === 0 && <p className="mt-3 text-muted">{t('helpers.boardEmpty')}</p>}
        <ol className="mt-3 divide-y divide-line">
          {board?.entries.map((e, i) => (
            <li key={`${e.nickname}-${i}`} className="flex items-center justify-between py-2.5">
              <span className="flex items-center gap-3">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-amber text-ink' : 'bg-blue-50 text-blue-700'}`}>
                  {i + 1}
                </span>
                <span className="font-semibold">{e.nickname}</span>
              </span>
              <span className="font-bold tabular-nums text-blue">{e.points}</span>
            </li>
          ))}
        </ol>
      </section>
    </Page>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-xl bg-white/10 p-3">
      <p className="text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
      <p className="text-xs text-night-muted">{label}</p>
    </div>
  );
}
